'use client'

import { useState, useEffect, useRef, useTransition } from 'react'
import { Input, Label, TextArea, TextField, toast } from '@heroui/react'
import { TurnstileWidget } from '@/components/forms/turnstile-widget'
import { Button } from '@/components/ui/button'
import { CompatSelect as Select, ListBoxItem } from '@/components/ui/compat-select'
import { Icon } from '@/components/ui/icon'
import { pushFormSubmit } from '@/lib/analytics/funnel-events'
import {
  submitContactInquiry,
  getVehicleYears,
  getVehicleMakes,
  getVehicleModels,
} from '@/lib/services/actions/inquiry'

interface ColourConsultFormProps {
  locationId: string
  locationEmail: string
  onSubmitted?: () => void
}

type VehicleEntryMode = 'select' | 'vin'

export function ColourConsultForm({ locationId, locationEmail, onSubmitted }: ColourConsultFormProps) {
  const [isPending, startTransition] = useTransition()
  const [submitted, setSubmitted] = useState(false)
  // Synchronous reentrancy lock: see book-now-form.tsx for why this can't
  // just be `isPending` (a fast double click beats the next render).
  const inFlightRef = useRef(false)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [vehicleEntryMode, setVehicleEntryMode] = useState<VehicleEntryMode>('select')
  const [vehicleYear, setVehicleYear] = useState('')
  const [vehicleMake, setVehicleMake] = useState('')
  const [vehicleModel, setVehicleModel] = useState('')
  const [vin, setVin] = useState('')
  const [message, setMessage] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  // Bumped after a rejected submit: the token is single-use, so the widget has
  // to draw a fresh one before the customer can try again.
  const [turnstileNonce, setTurnstileNonce] = useState(0)

  const [years, setYears] = useState<number[]>([])
  const [makes, setMakes] = useState<string[]>([])
  const [models, setModels] = useState<string[]>([])

  useEffect(() => {
    getVehicleYears().then(setYears).catch(() => {})
  }, [])

  useEffect(() => {
    if (vehicleYear) {
      setVehicleMake('')
      setVehicleModel('')
      setMakes([])
      setModels([])
      getVehicleMakes(Number(vehicleYear)).then(setMakes).catch(() => {})
    }
  }, [vehicleYear])

  useEffect(() => {
    if (vehicleYear && vehicleMake) {
      setVehicleModel('')
      setModels([])
      getVehicleModels(Number(vehicleYear), vehicleMake).then(setModels).catch(() => {})
    }
  }, [vehicleYear, vehicleMake])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inFlightRef.current) return
    inFlightRef.current = true

    startTransition(async () => {
      try {
        const result = await submitContactInquiry({
          name,
          email,
          phone: phone || undefined,
          vehicleYear: vehicleEntryMode === 'select' && vehicleYear ? Number(vehicleYear) : undefined,
          vehicleMake: vehicleEntryMode === 'select' ? vehicleMake || undefined : undefined,
          vehicleModel: vehicleEntryMode === 'select' ? vehicleModel || undefined : undefined,
          vin: vehicleEntryMode === 'vin' ? vin || undefined : undefined,
          serviceInterest: 'Colour Consult',
          message: message || undefined,
          shopLocationId: locationId,
          source: 'colour-consult',
          turnstileToken: turnstileToken ?? undefined,
        })

        if (result.success) {
          if (result.data?.id && result.data.id !== 'ignored')
            pushFormSubmit('colour-consult', result.data.id, { email, phone })
          toast.success('Request Sent', {
            description: "We'll be in touch to book your colour consult."
          })
          setSubmitted(true)
          onSubmitted?.()
        } else {
          setTurnstileNonce((n) => n + 1)
          toast.danger('Error', {
            description: result.error || 'Something went wrong.'
          })
        }
      } finally {
        inFlightRef.current = false
      }
    })
  }

  if (submitted) {
    return (
      <div className='py-8 text-center' role='status' aria-live='polite'>
        <Icon icon='check-circle' className='mx-auto mb-3 text-4xl text-success' />
        <h3 className='mb-1 text-lg font-bold text-foreground'>Request sent</h3>
        <p className='text-sm text-muted'>
          We received your colour consult request and will reach out to book a time.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-4' aria-label='Colour consult request form'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <TextField value={name} onChange={setName} isRequired>
          <Label>Name</Label>
          <Input variant='outline' />
        </TextField>
        <TextField value={email} onChange={setEmail} isRequired>
          <Label>Email</Label>
          <Input type='email' variant='outline' />
        </TextField>
      </div>

      <TextField value={phone} onChange={setPhone}>
        <Label>Phone</Label>
        <Input type='tel' variant='outline' />
      </TextField>

      <div>
        <div className='mb-2 flex items-center gap-2'>
          <button
            type='button'
            onClick={() => setVehicleEntryMode('select')}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
              vehicleEntryMode === 'select'
                ? 'bg-accent text-accent-foreground'
                : 'bg-surface-tertiary text-muted hover:text-foreground'
            }`}
          >
            Year / Make / Model
          </button>
          <button
            type='button'
            onClick={() => setVehicleEntryMode('vin')}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
              vehicleEntryMode === 'vin'
                ? 'bg-accent text-accent-foreground'
                : 'bg-surface-tertiary text-muted hover:text-foreground'
            }`}
          >
            I have my VIN
          </button>
        </div>

        {vehicleEntryMode === 'select' ? (
          <div className='grid grid-cols-3 gap-3'>
            <Select
              label='Year'
              aria-label='Vehicle year'
              selectedKeys={vehicleYear ? [vehicleYear] : []}
              onSelectionChange={(keys) => setVehicleYear((Array.from(keys)[0] as string) || '')}
              variant='outline'
            >
              {years.map((y) => (
                <ListBoxItem key={String(y)}>{String(y)}</ListBoxItem>
              ))}
            </Select>
            <Select
              label='Make'
              aria-label='Vehicle make'
              selectedKeys={vehicleMake ? [vehicleMake] : []}
              onSelectionChange={(keys) => setVehicleMake((Array.from(keys)[0] as string) || '')}
              variant='outline'
              isDisabled={!vehicleYear}
            >
              {makes.map((m) => (
                <ListBoxItem key={m}>{m}</ListBoxItem>
              ))}
            </Select>
            <Select
              label='Model'
              aria-label='Vehicle model'
              selectedKeys={vehicleModel ? [vehicleModel] : []}
              onSelectionChange={(keys) => setVehicleModel((Array.from(keys)[0] as string) || '')}
              variant='outline'
              isDisabled={!vehicleMake}
            >
              {models.map((m) => (
                <ListBoxItem key={m}>{m}</ListBoxItem>
              ))}
            </Select>
          </div>
        ) : (
          <TextField value={vin} onChange={(v) => setVin(v.toUpperCase())}>
            <Label>VIN</Label>
            <Input variant='outline' maxLength={17} placeholder='17-character VIN' />
          </TextField>
        )}
      </div>

      {/* No live upload here: a form this small doesn't carry the abuse/
          moderation surface of accepting arbitrary public file uploads.
          Email is the existing, already-staffed intake channel. */}
      <div className='flex items-start gap-2 rounded-md border-l-4 border-accent bg-surface p-3 text-sm text-foreground'>
        <Icon icon='info' className='mt-0.5 shrink-0 text-accent' />
        <p>
          Have photos of your vehicle or a look you want to match? Email them to{' '}
          <a href={`mailto:${locationEmail}`} className='font-semibold underline'>
            {locationEmail}
          </a>{' '}
          with your name after submitting, and we&apos;ll have them ready for your consult.
        </p>
      </div>

      <TextField value={message} onChange={setMessage}>
        <Label>Message</Label>
        <TextArea variant='outline' rows={3} placeholder="Tell us about the look you're going for..." />
      </TextField>

      <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileNonce} />

      <Button type='submit' size='lg' className='w-full' isLoading={isPending} isDisabled={isPending}>
        {isPending ? 'Sending...' : 'Send Consult Request'}
      </Button>
    </form>
  )
}
